import { Col, Row, Spin, Table, Typography } from "antd";
import Column from "antd/es/table/Column";
import { formatVND, roundToTwo } from "../../helpers/common";
import { useEffect, useRef, useState } from "react";
import { duLieuThuNhapCaNhan } from "../../services/danhmuc";
import { layThuNhapLuongThangCaNhan } from "../../services/luong";
import { ThuNhapLuongThangInfo, ChiTietThuNhapBangItem } from "../../models/luong/Luong";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar, getElementAtEvent } from "react-chartjs-2";
import { hideLoadingScreen, showLoadingScreen } from '../../store/reducers/loading';
import { useDispatch } from 'react-redux';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const { Text } = Typography;

const ThuNhapCaNhanBox = ({ year }: { year: number }) => {
    const [loadingDetail, setLoadingDetail] = useState<boolean>(false);
    const [monthToGetData, setMonthToGetData] = useState<number | null>(null);
    const [thuNhapLuongThang, setThuNhapLuongThang] = useState<ThuNhapLuongThangInfo | null>(null);
    const [dataThuNhapChart, setDataThuNhapChart] = useState({
        labels: ['Tháng 1', 'Tháng 2', 'Tháng 3', 'Tháng 4', 'Tháng 5', 'Tháng 6', 'Tháng 7', 'Tháng 8', 'Tháng 9', 'Tháng 10', 'Tháng 11', 'Tháng 12'],
        datasets: [
            {
                label: 'Thu nhập',
                data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
                backgroundColor: 'rgb(53, 162, 235)',
                stack: 'Stack 0',
            },
            {
                label: 'Thực nhận',
                data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
                backgroundColor: 'rgb(255, 206, 86)',
                stack: 'Stack 1',
            },
        ],
    });
    const dispatch = useDispatch();

    const options = {
        responsive: true,
        plugins: {
            title: {
                display: false,
                text: ''
            }
        },
        scales: {
            x: {
                border: { display: true },
                grid: { display: false }
            },
            y: {
                title: { display: true, text: 'Triệu đồng' }
            }
        }
    };

    const layDuLieuThuNhapChart = async (selectedYear: number) => {
        try {
            dispatch(showLoadingScreen());
            var rs = await duLieuThuNhapCaNhan(selectedYear);
            const sorted = [...(rs || [])].sort((a, b) => a.thang - b.thang);
            setDataThuNhapChart({
                labels: ['Tháng 1', 'Tháng 2', 'Tháng 3', 'Tháng 4', 'Tháng 5', 'Tháng 6', 'Tháng 7', 'Tháng 8', 'Tháng 9', 'Tháng 10', 'Tháng 11', 'Tháng 12'],
                datasets: [
                    {
                        label: 'Thu nhập',
                        data: sorted.map((item) => roundToTwo(item?.tongThuNhap / 1000000)),
                        backgroundColor: 'rgb(53, 162, 235)',
                        stack: 'Stack 0',
                    },
                    {
                        label: 'Thực nhận',
                        data: sorted.map((item) => roundToTwo(item?.thucNhan / 1000000)),
                        backgroundColor: 'rgb(255, 206, 86)',
                        stack: 'Stack 1',
                    },
                ],
            });

            // Tự động chọn tháng mặc định nếu chưa chọn
            const currentYear = new Date().getFullYear();
            const currentMonth = new Date().getMonth() + 1;
            let defaultMonth: number | null = null;
            if (selectedYear === currentYear) {
                defaultMonth = currentMonth;
            } else {
                const monthsWithData = sorted.filter(x => (x.tongThuNhap || 0) > 0 || (x.thucNhan || 0) > 0);
                if (monthsWithData.length > 0) {
                    defaultMonth = monthsWithData[monthsWithData.length - 1].thang;
                } else {
                    defaultMonth = 1;
                }
            }
            if (defaultMonth) {
                setMonthToGetData(defaultMonth);
            }

            dispatch(hideLoadingScreen());
        } catch (err: any) {
            dispatch(hideLoadingScreen());
        }
    };

    const layChiTietLuongThang = async (month: number, year: number) => {
        try {
            setLoadingDetail(true);
            var rs = await layThuNhapLuongThangCaNhan(month, year);
            setThuNhapLuongThang(rs);
        } catch (err: any) {
            setThuNhapLuongThang(null);
        } finally {
            setLoadingDetail(false);
        }
    };

    const chartRef = useRef(null);
    const onClick = (event: any) => {
        if (chartRef.current) {
            const elements = getElementAtEvent(chartRef?.current, event);
            if (elements && elements.length > 0) {
                const idx = elements[0]?.index;
                if (idx !== undefined) {
                    setMonthToGetData(idx + 1);
                }
            }
        }
    };

    useEffect(() => {
        layDuLieuThuNhapChart(year);
    }, [year]);

    useEffect(() => {
        if (monthToGetData != null && monthToGetData > 0) {
            layChiTietLuongThang(monthToGetData, year);
        }
    }, [monthToGetData, year]);

    // Xây dựng danh sách dòng chi tiết theo đúng cấu trúc bảng mẫu
    const buildTableRows = (): ChiTietThuNhapBangItem[] => {
        if (!monthToGetData) return [];

        // Nếu API đã trả về cấu trúc bảng sẵn từ backend
        if (thuNhapLuongThang?.dsChiTietBang && thuNhapLuongThang.dsChiTietBang.length > 0) {
            return thuNhapLuongThang.dsChiTietBang;
        }

        const thangTruoc = monthToGetData === 1 ? 12 : monthToGetData - 1;
        const namTruoc = monthToGetData === 1 ? year - 1 : year;
        const ds = thuNhapLuongThang?.dsKhoan || [];

        const getSoTien = (loai: number) => ds.find(x => x.loai === loai)?.soTien ?? 0;

        const tienLCB = getSoTien(2);
        const tienPhuCap = getSoTien(3);
        const tienThemGio = getSoTien(4);
        const tienHTNV = getSoTien(5);
        const tienBSTBKS = getSoTien(6);
        const tienThuLaoKCT = getSoTien(7);
        const tienPhuCapTGV = getSoTien(8);
        const tienKhenThuong = getSoTien(1);

        const nhomI = tienLCB + tienPhuCap + tienThemGio !== 0 ? (tienLCB + tienPhuCap + tienThemGio) : 0;
        const nhomII = tienHTNV + tienBSTBKS;
        const nhomIII = tienBSTBKS !== 0 ? tienBSTBKS : (tienLCB + tienPhuCap);
        const nhomIV = tienThuLaoKCT + tienPhuCapTGV + tienKhenThuong;
        const nhomV = thuNhapLuongThang?.tienThueTNCN ?? 0;
        const nhomVI = thuNhapLuongThang?.thucNhan ?? (thuNhapLuongThang?.tongThuNhap ?? (nhomI + nhomII + nhomIII + nhomIV - nhomV));
        const hasDieuChinh = tienLCB !== 0 || tienPhuCap !== 0 || tienThemGio !== 0;
        const noiDungNhomI = hasDieuChinh
            ? `Điều chỉnh LCB, phụ cấp tháng ${thangTruoc}/${namTruoc} đã tạm ứng (5+6+9)`
            : 'Điều chỉnh LCB, phụ cấp';

        return [
            {
                tt: 'I',
                noiDung: noiDungNhomI,
                soTien: nhomI,
                isHeader: true,
                level: 0,
            },
            {
                tt: '1',
                noiDung: 'Kết quả hoàn thành nhiệm vụ',
                chiTiet: '-',
                level: 1,
            },
            {
                tt: '2',
                noiDung: 'Chấm công tháng',
                chiTiet: '-',
                level: 1,
            },
            {
                tt: '3',
                noiDung: 'LCB được nhận (hệ số * 4.730.000 đồng)',
                soTien: tienLCB,
                level: 1,
            },
            {
                tt: '4',
                noiDung: 'LCB đã ứng',
                soTien: tienLCB,
                level: 1,
            },
            {
                tt: '5',
                noiDung: 'LCB còn được nhận (3-4)',
                soTien: 0,
                level: 1,
            },
            {
                tt: '6',
                noiDung: 'Tiền lương làm thêm giờ (nếu có)',
                soTien: tienThemGio,
                level: 1,
            },
            {
                tt: '7',
                noiDung: 'Phụ cấp được nhận',
                soTien: tienPhuCap,
                level: 1,
            },
            {
                tt: '8',
                noiDung: 'Phụ cấp đã ứng',
                soTien: tienPhuCap,
                level: 1,
            },
            {
                tt: '9',
                noiDung: 'Phụ cấp còn được nhận (7-8)',
                soTien: 0,
                level: 1,
            },
            {
                tt: 'II',
                noiDung: `Lương bổ sung/lương HTNV tháng ${thangTruoc}/${namTruoc}`,
                soTien: nhomII,
                isHeader: true,
                level: 0,
            },
            {
                tt: 'III',
                noiDung: `Tạm ứng lương và phụ cấp tháng ${monthToGetData}/${year} (12+13)`,
                soTien: nhomIII,
                isHeader: true,
                level: 0,
            },
            {
                tt: '10',
                noiDung: 'LCB được nhận (hệ số * 4.730.000 đồng)',
                soTien: tienLCB,
                level: 1,
            },
            {
                tt: '11',
                noiDung: 'Các khoản giảm trừ',
                soTien: 0,
                level: 1,
            },
            {
                tt: '',
                noiDung: `- BHXH tháng ${monthToGetData}/${year}`,
                chiTiet: '',
                level: 2,
            },
            {
                tt: '12',
                noiDung: 'LCB tạm ứng được nhận (10-11)',
                soTien: tienLCB,
                level: 1,
            },
            {
                tt: '13',
                noiDung: 'Phụ cấp tạm ứng được nhận',
                soTien: tienPhuCap,
                level: 1,
            },
            {
                tt: 'IV',
                noiDung: 'Các khoản thu nhập khác',
                soTien: nhomIV,
                isHeader: true,
                level: 0,
            },
            {
                tt: '14',
                noiDung: 'Thù lao không chuyên trách',
                soTien: tienThuLaoKCT,
                level: 1,
            },
            {
                tt: '15',
                noiDung: 'Phụ cấp tổ giúp việc',
                soTien: tienPhuCapTGV,
                level: 1,
            },
            {
                tt: '16',
                noiDung: 'Khen thưởng và các khoản phát sinh khác',
                soTien: tienKhenThuong,
                level: 1,
            },
            {
                tt: 'V',
                noiDung: 'Thuế TNCN',
                soTien: nhomV,
                isHeader: true,
                level: 0,
            },
            {
                tt: '17',
                noiDung: 'Thuế TNCN',
                soTien: nhomV,
                level: 1,
            },
            {
                tt: 'VI',
                noiDung: 'Tổng cộng thu nhập (I+II+III+IV-V)',
                soTien: nhomVI,
                isHeader: true,
                level: 0,
            },
            // {
            //     tt: '',
            //     noiDung: `Ghi chú (nếu có): ${thuNhapLuongThang?.ghiChu || ''}`,
            //     chiTiet: '',
            //     level: 0,
            // },
        ];
    };

    const tableData = buildTableRows().map((item, idx) => ({ key: idx, ...item }));

    return (
        <Row className="form-group" gutter={16}>
            <Col xs={24} xl={10}>
                <b style={{ fontSize: "18px", color: "#071C50" }}>Dữ liệu thu nhập năm {year}</b>
                <div style={{ width: "100%", marginTop: 8 }}>
                    <Bar
                        ref={chartRef}
                        onClick={onClick}
                        data={dataThuNhapChart}
                        options={options}
                        style={{ cursor: "pointer" }}
                    />
                </div>
                <div style={{ textAlign: "center", color: "#888", fontSize: 12, marginTop: 6 }}>
                    Nhấn vào cột để xem chi tiết thu nhập tháng
                </div>
            </Col>

            <Col xs={24} xl={14}>
                <style>{`
                    .income-table-header-row {
                        background-color: #f2f5f9;
                    }
                    .income-table-header-row td {
                        font-weight: bold !important;
                        color: #002060 !important;
                    }
                `}</style>
                <b style={{ fontSize: "18px", color: "#071C50" }}>
                    Chi tiết thu nhập
                    {monthToGetData ? ` tháng ${monthToGetData}/${year}` : ""}
                </b>
                {!monthToGetData ? (
                    <div style={{ marginTop: 36, color: "#aaa", textAlign: "center" }}>
                        Chọn một tháng trên biểu đồ để xem chi tiết
                    </div>
                ) : (
                    <Spin spinning={loadingDetail}>
                        <div style={{ marginTop: 8, overflowX: "auto" }}>
                            <Table
                                dataSource={tableData}
                                pagination={false}
                                size="small"
                                bordered
                                locale={{ emptyText: "Không có dữ liệu lương tháng này" }}
                                rowClassName={(record: any) => {
                                    if (record.isHeader) return "income-table-header-row";
                                    return "";
                                }}
                            >
                                <Column
                                    title={
                                        <div style={{ textAlign: "center", fontWeight: "bold", background: "#e2efda", margin: "-8px -8px", padding: "8px" }}>
                                            TT
                                        </div>
                                    }
                                    dataIndex="tt"
                                    key="tt"
                                    width={60}
                                    align="center"
                                    render={(val: string, record: ChiTietThuNhapBangItem) => (
                                        <span style={{
                                            fontWeight: record.isHeader || ['5', '6', '9', '12', '13'].includes(val) ? 'bold' : 'normal',
                                            color: record.isHeader ? '#002060' : undefined
                                        }}>
                                            {val}
                                        </span>
                                    )}
                                />
                                <Column
                                    title={
                                        <div style={{ textAlign: "center", fontWeight: "bold", background: "#e2efda", margin: "-8px -8px", padding: "8px" }}>
                                            Nội dung
                                        </div>
                                    }
                                    dataIndex="noiDung"
                                    key="noiDung"
                                    render={(val: string, record: ChiTietThuNhapBangItem) => (
                                        <span style={{
                                            paddingLeft: record.level === 2 ? 24 : record.level === 1 ? 12 : 0,
                                            fontWeight: record.isHeader || ['5', '6', '9', '12', '13'].includes(record.tt) ? 'bold' : 'normal',
                                            color: record.isHeader ? '#002060' : undefined,
                                            fontStyle: record.level === 2 ? 'italic' : 'normal'
                                        }}>
                                            {val}
                                        </span>
                                    )}
                                />
                                <Column
                                    title={
                                        <div style={{ textAlign: "center", fontWeight: "bold", background: "#e2efda", margin: "-8px -8px", padding: "8px" }}>
                                            Chi tiết
                                        </div>
                                    }
                                    dataIndex="soTien"
                                    key="chiTiet"
                                    width={160}
                                    align="right"
                                    render={(val: number | undefined, record: ChiTietThuNhapBangItem) => {
                                        if (record.soTien !== undefined && record.soTien !== null) {
                                            const isBold = record.isHeader || ['5', '6', '9', '12', '13'].includes(record.tt);
                                            const isBlue = record.isHeader;
                                            return (
                                                <span style={{
                                                    fontWeight: isBold ? 'bold' : 'normal',
                                                    color: isBlue ? '#002060' : undefined
                                                }}>
                                                    {formatVND(record.soTien)}
                                                </span>
                                            );
                                        }
                                        if (record.chiTiet != null && record.chiTiet !== '') {
                                            return <span style={{ textAlign: "center", display: "block" }}>{record.chiTiet}</span>;
                                        }
                                        return null;
                                    }}
                                />
                            </Table>
                        </div>
                    </Spin>
                )}
            </Col>
        </Row>
    );
};

export default ThuNhapCaNhanBox;
